import { useState, useEffect, useRef } from 'react';
import { useIsMaximized } from '@/hooks/useIsMaximized';
import { useLanguage } from '@/lib/LanguageContext';

const SECTIONS = [
  { id: 'inicio', key: 'indicator.inicio' },
  { id: 'sectores', key: 'indicator.sectores' },
  { id: 'como-funciona', key: 'indicator.comoFunciona' },
  { id: 'solucion', key: 'indicator.solucion' },
  { id: 'verifactu', key: 'indicator.verifactu' },
  { id: 'tpv-negocio', key: 'indicator.tpv' },
  { id: 'faq', key: 'indicator.faq' },
];

export default function SectionIndicator() {
  const [visible, setVisible] = useState(false);
  const [activeId, setActiveId] = useState('inicio');
  const observerRef = useRef(null);
  const isMaximized = useIsMaximized();
  const { t } = useLanguage();

  useEffect(() => {
    const onScroll = () => {
      const footer = document.querySelector('footer');
      const footerVisible = footer
        ? footer.getBoundingClientRect().top < window.innerHeight
        : false;
      setVisible(window.scrollY > 400 && !footerVisible);
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-40% 0px -40% 0px', threshold: 0 }
    );
    observerRef.current = observer;

    const observedIds = new Set();
    const observeSections = () => {
      SECTIONS.forEach(({ id }) => {
        if (observedIds.has(id)) return;
        const el = document.getElementById(id);
        if (el) {
          observer.observe(el);
          observedIds.add(id);
        }
      });
    };

    observeSections();

    // Las secciones dentro de LazySection se montan tarde; detectamos su aparición
    const mutationObserver = new MutationObserver(() => observeSections());
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      mutationObserver.disconnect();
      observer.disconnect();
    };
  }, []);

  const handleClick = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div
      className={`hidden lg:flex fixed right-6 top-1/2 -translate-y-1/2 z-40 transition-opacity duration-300 ${
        visible && isMaximized ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
    >
      <div className="bg-white rounded-xl shadow-lg shadow-black/8 border border-gray-100 px-3 py-3 space-y-2">
        {SECTIONS.map(({ id, key }) => (
          <button
            key={id}
            onClick={() => handleClick(id)}
            className="flex items-center gap-2.5 group"
          >
            <span
              className={`w-1.5 h-1.5 rounded-full transition-colors duration-200 ${
                activeId === id ? 'bg-black' : 'bg-gray-300 group-hover:bg-gray-400'
              }`}
            />
            <span
              className={`text-xs font-medium transition-colors duration-200 whitespace-nowrap ${
                activeId === id ? 'text-black' : 'text-gray-400 group-hover:text-gray-600'
              }`}
            >
              {t(key)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}